import React, {useCallback, useEffect, useState} from 'react';
import {WatchPhrase, JobStatus} from '../../schemas';
import {Loader} from '../../components/Loader';
import GridFunction from '../../components/DocumentGrid';
import DocumentPageView, {DocumentPalettes, HighlightOption} from '../PrimaryDocumentPage/DocumentPageContainerView';
import DocumentToolsPanel from '../../components/DocumentToolsPanel';
import {useEnhancedDocumentsContext} from '../../contexts/doc_contexts';
import {GridView, useNavigationContext} from '../../contexts/NavContext/nav-context';
import {useJobContext} from '../../contexts/job_context';
import {useFolderContext} from '../../contexts/folder_context';
import DashboardHeader, {useDashboardHeader} from '../../components/DashboardHeader';
import ChatInterface from "../../components/ChatInterface/ChatInterface";
import {useUserContext} from "../../contexts/user_context";
import {ManageGroupTags} from "../PrimaryFoldersPage/popup-update-codes";
import {BsStars} from "react-icons/bs";
import {FiDownload} from "react-icons/fi";
import {AIStateProvider} from "../../contexts/ai_chat_state_context";
import {DocumentSpotlightProvider} from "../../contexts/document_spotlight_context";
import {motion, AnimatePresence} from 'framer-motion';

import PaletteProduction from "../../components/Palettes/PaletteProduction";
import PalettePanel from "../../components/Palettes/PalettePanel";
import ResizableRightPanel from "../../components/ResizableRightPanel";
import JobProgressList from "../../components/Jobs";

// Sidebar icon button component
const SidebarIconButton: React.FC<{
    icon: React.ReactNode;
    title: string;
    isActive: boolean;
    onClick: () => void;
    onMouseEnter?: () => void;
}> = ({icon, title, isActive, onClick, onMouseEnter}) => (
    <div className="p-2">
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            title={title}
            className={[
                "h-8 w-8 grid place-items-center rounded-md",
                isActive
                    ? "bg-indigo-400 text-white"
                    : "text-gray-300 hover:text-white hover:bg-[#3a3a4a]",
                "transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-slate-500",
            ].join(" ")}
        >
            <span className="text-[16px] leading-none">{icon}</span>
        </button>
    </div>
);

// Panel content that switches based on active palette
const ExplorePanelContent: React.FC<{ activePalette: string | null }> = ({activePalette}) => {
    if (activePalette === 'request') {
        return (
            <PalettePanel title="Assistant" isOpen={true}>
                <ChatInterface/>
            </PalettePanel>
        );
    }
    if (activePalette === 'production') {
        return (
            <PalettePanel title="Export Production" isOpen={true}>
                <PaletteProduction/>
            </PalettePanel>
        );
    }
    return null;
};

// Sidebar icons for explore page
const ExploreSidebarIcons: React.FC<{
    activePalette: string | null;
    setActivePalette: (id: string | null) => void;
    pinnedPalette: string | null;
    setPinnedPalette: (id: string | null) => void;
}> = ({activePalette, setActivePalette, pinnedPalette, setPinnedPalette}) => {
    const {openLeftPanel, closeLeftPanel, hoverOpenLeftPanel, leftPanelWidth} = useDashboardHeader();
    const {completeUserTutorial} = useUserContext()!;
    const {customFlow} = useNavigationContext()!;
    const hasInitialized = React.useRef(false);

    // Open panel on mount if activePalette is set
    useEffect(() => {
        if (activePalette && !hasInitialized.current) {
            hasInitialized.current = true;
            // Small delay to ensure context is ready
            const timer = setTimeout(() => {
                openLeftPanel();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [activePalette, openLeftPanel]);

    // Handle first time flow
    useEffect(() => {
        if (customFlow.firstTimeFlow) {
            setActivePalette('request');
            setPinnedPalette('request');
            completeUserTutorial();
        }
    }, []);

    // When the panel closes (hover-leave with nothing pinned), clear hover state.
    // When the panel is open but user hovered over a non-pinned icon, revert to
    // the pinned palette once they stop interacting (panel closes).
    const prevWidthRef = React.useRef(leftPanelWidth);
    useEffect(() => {
        if (prevWidthRef.current > 0 && leftPanelWidth === 0) {
            // Panel just closed — restore hover state to the pinned palette
            setActivePalette(pinnedPalette);
        }
        prevWidthRef.current = leftPanelWidth;
    }, [leftPanelWidth, pinnedPalette, setActivePalette]);

    const handleClick = useCallback((id: string) => {
        // Decision is based on the PINNED palette, not the hovered-active one.
        // Clicking the already-pinned icon → close.
        // Clicking any other icon (or no pin) → pin + switch to it (keeps panel open).
        if (pinnedPalette === id) {
            setPinnedPalette(null);
            setActivePalette(null);
            closeLeftPanel();
        } else {
            setPinnedPalette(id);
            setActivePalette(id);
            openLeftPanel();
        }
    }, [pinnedPalette, openLeftPanel, closeLeftPanel, setActivePalette, setPinnedPalette]);

    const handleHover = useCallback((id: string) => {
        setActivePalette(id);
        hoverOpenLeftPanel();
    }, [hoverOpenLeftPanel, setActivePalette]);

    return (
        <>
            <SidebarIconButton
                icon={<BsStars/>}
                title="Document Request"
                isActive={activePalette === 'request'}
                onClick={() => handleClick('request')}
                onMouseEnter={() => handleHover('request')}
            />
            <SidebarIconButton
                icon={<FiDownload/>}
                title="Export Production"
                isActive={activePalette === 'production'}
                onClick={() => handleClick('production')}
                onMouseEnter={() => handleHover('production')}
            />
        </>
    );
};

// ── Animation variants ──────────────────────────────────────────────────

const gridVariants = {
    visible: {width: '100%', opacity: 1, transition: {duration: 0.35, ease: 'easeInOut' as const}},
    hidden: {width: 0, opacity: 0, transition: {duration: 0.3, ease: 'easeInOut' as const}},
};

const expandedDocVariants = {
    initial: {opacity: 0, x: -20},
    animate: {opacity: 1, x: 0, transition: {duration: 0.3, ease: 'easeOut' as const, delay: 0.05}},
    exit: {opacity: 0, x: -20, transition: {duration: 0.2}},
};

// Wrapper component to manage palette state
const PrimaryDocumentExplorePageInner: React.FC<{ initialDocExpanded?: boolean }> = ({initialDocExpanded}) => {
    const {
        document,
        setDocument,
    } = useEnhancedDocumentsContext()!;

    const {getNavHeader, isDocExpanded, setIsDocExpanded} = useNavigationContext()!;
    const {activeJobs} = useJobContext()!;
    const currentFolder = useFolderContext()?.currentFolder;
    const failedOrActiveJobs = activeJobs
        .filter(j => j.status === JobStatus.PROCESSING || j.status === JobStatus.QUEUED || (j.warnings && j.warnings.length > 0))
        .map(j => {
            if (!j.warnings?.length || !currentFolder?.id) return j;
            const folderWarnings = j.warnings.filter(w => w.folder_id === currentFolder.id);
            if (folderWarnings.length === j.warnings.length) return j;
            if (folderWarnings.length === 0) return {...j, warnings: undefined};
            return {...j, warnings: folderWarnings};
        })
        .filter(j => j.status === JobStatus.PROCESSING || j.status === JobStatus.QUEUED || (j.warnings && j.warnings.length > 0));
    const [loading] = useState(false);
    const [highlightOptions, setHighlightOptions] = useState<HighlightOption[]>([]);
    const [activePalette, setActivePalette] = useState<string | null>('request');
    const [pinnedPalette, setPinnedPalette] = useState<string | null>('request');

    // Initialize expanded state from prop (for deep-link SingleDocumentView)
    useEffect(() => {
        if (initialDocExpanded && document) {
            setIsDocExpanded(true);
        }
    }, [initialDocExpanded]); // eslint-disable-line

    // Sync URL with active document — single source of truth.
    // Whenever the selected document changes (expand, next/prev, click),
    // update the URL to include/remove the docId.
    useEffect(() => {
        const path = window.location.pathname;
        const uuids = path.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g) || [];
        if (document) {
            if (uuids.length >= 3 && uuids[uuids.length - 1] !== document.id) {
                // Replace existing docId
                const lastUuid = uuids[uuids.length - 1];
                window.history.replaceState(null, '', path.slice(0, path.lastIndexOf(lastUuid)) + document.id);
            } else if (uuids.length === 2) {
                // Append docId
                window.history.replaceState(null, '', `${path}/${document.id}`);
            }
        } else if (uuids.length >= 3) {
            // Document deselected — strip docId from URL
            const lastUuid = uuids[uuids.length - 1];
            const cleaned = path.slice(0, path.lastIndexOf(lastUuid) - 1);
            window.history.replaceState(null, '', cleaned);
        }
    }, [document?.id]);

    const setActiveWatchPhrasesCallback = useCallback((watchPhrases: WatchPhrase[]) => {
        const newOptions = watchPhrases.map(watchPhrase => ({
            label: watchPhrase.phrase,
            color: watchPhrase.color,
            id: watchPhrase.id,
        }));
        setHighlightOptions([...newOptions]);
    }, []);


    // Escape key handler: collapse expanded view, or deselect document
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isDocExpanded) {
                    setIsDocExpanded(false);
                } else if (document) {
                    setDocument(null);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDocExpanded, document, setIsDocExpanded, setDocument]);

    const handleExpand = useCallback(() => {
        setIsDocExpanded(true);
    }, [setIsDocExpanded]);

    const handleCollapse = useCallback(() => {
        setIsDocExpanded(false);
    }, [setIsDocExpanded]);

    const handleClose = useCallback(() => {
        setIsDocExpanded(false);
        setDocument(null);
        // Strip docId from URL (only if 3 UUIDs present = groupId/folderId/docId)
        const path = window.location.pathname;
        const uuids = path.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g) || [];
        if (uuids.length >= 3) {
            const lastUuid = uuids[uuids.length - 1];
            const cleaned = path.slice(0, path.lastIndexOf(lastUuid) - 1);
            window.history.replaceState(null, '', cleaned);
        }
    }, [setIsDocExpanded, setDocument]);

    return (
        <DocumentSpotlightProvider>
        <AIStateProvider>
            <DashboardHeader
                sidebarIcons={<ExploreSidebarIcons activePalette={activePalette} setActivePalette={setActivePalette} pinnedPalette={pinnedPalette} setPinnedPalette={setPinnedPalette}/>}
                panelContent={<ExplorePanelContent activePalette={activePalette}/>}
            >
                {loading && (
                    <div className="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center z-50"
                         style={{backgroundColor: 'rgba(255, 255, 255, 0.7)'}}>
                        <Loader/>
                    </div>
                )}

                <div className="flex h-full w-full min-h-0">
                    {/* ── Left/center column: header + grid area (full vertical space) ── */}
                    <motion.div
                        className="flex flex-col overflow-hidden min-w-0"
                        variants={gridVariants}
                        animate={isDocExpanded ? 'hidden' : 'visible'}
                        initial={false}
                    >
                        {/* Header + tag manager — now lives within the grid column */}
                        <div className="shrink-0">
                            <div className="flex justify-between items-center gap-2 px-2">
                                {getNavHeader()}
                                <ManageGroupTags/>
                            </div>
                        </div>

                        {/* Job progress (processing + failed with retry) */}
                        {failedOrActiveJobs.length > 0 && (
                            <div className="shrink-0 px-2 pb-1">
                                <JobProgressList jobs={failedOrActiveJobs} compact/>
                            </div>
                        )}

                        {/* Grid */}
                        <div
                            className="flex-1 min-h-0 overflow-auto"
                            style={{visibility: isDocExpanded ? 'hidden' : 'visible'}}
                        >
                            <GridFunction gridView={GridView.Default}/>
                        </div>
                    </motion.div>

                    {/* Main content area (expanded doc view + right panel) */}
                    <div className="flex flex-1 min-h-0">

                        {/* ── Expanded document view: center = title+nav+viewer, right = palettes ── */}
                        <AnimatePresence>
                            {document && isDocExpanded && (
                                <motion.div
                                    key="expanded-doc"
                                    className="flex flex-1 min-h-0 min-w-0"
                                    variants={expandedDocVariants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                >
                                    {/* Center: Title bar + document viewer */}
                                    <div className="flex-1 min-w-0 flex flex-col">
                                        <DocumentToolsPanel
                                            doc={document}
                                            onClose={handleClose}
                                            onCollapse={handleCollapse}
                                            showNav={true}
                                        >
                                            <DocumentPageView
                                                highlightOptions={highlightOptions}
                                                setHighlightOptions={setHighlightOptions}
                                                showBackButton={false}
                                                showPalettes={false}
                                            />
                                        </DocumentToolsPanel>
                                    </div>

                                    {/* Right: Palettes (metadata, codes, watch list) */}
                                    <ResizableRightPanel isOpen={true} defaultWidthRatio={0.28} minWidth={320} maxWidth={560}>
                                        <div className="h-full overflow-auto bg-slate-50/80">
                                            <DocumentPalettes
                                                doc={document}
                                                setActiveWatchPhrasesCallback={setActiveWatchPhrasesCallback}
                                            />
                                        </div>
                                    </ResizableRightPanel>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* ── Side panel preview: shown when doc selected but NOT expanded ── */}
                        {!isDocExpanded && (
                            <ResizableRightPanel isOpen={!!document}>
                                {document && (
                                    <DocumentToolsPanel
                                        doc={document}
                                        onClose={() => setDocument(null)}
                                        onExpand={handleExpand}
                                        showNav={true}
                                    >
                                        <DocumentPageView
                                            highlightOptions={highlightOptions}
                                            setHighlightOptions={setHighlightOptions}
                                            setActiveWatchPhrasesCallback={setActiveWatchPhrasesCallback}
                                            showBackButton={false}
                                        />
                                    </DocumentToolsPanel>
                                )}
                            </ResizableRightPanel>
                        )}
                    </div>
                </div>
            </DashboardHeader>
        </AIStateProvider>
        </DocumentSpotlightProvider>
    );
};

interface PrimaryDocumentExplorePageProps {
    initialDocExpanded?: boolean;
}

const PrimaryDocumentExplorePage: React.FC<PrimaryDocumentExplorePageProps> = ({initialDocExpanded}) => {
    return <PrimaryDocumentExplorePageInner initialDocExpanded={initialDocExpanded}/>;
};

export default PrimaryDocumentExplorePage;
